import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import dateUtils from 'shared/src/date'
import useFilteredCalendarData from './useFilteredCalendarData'
import bookingSelectors from '../../store/selectors/booking'

import {
  processDate,
} from './utils'

const useCalendar = ({
  project,
  bookingform,
  admin = false,
  autoAdjustDate = true,
  days = 1,
  reloadDelay = 0,
  initialDate,
  config,
  calendarConfig,
  condensed,
  slotFilter,
  dataSelector,
  loadDataAction,
  onUpdateDate,
} = {}) => {

  const dispatch = useDispatch()

  const [ reloadTimestamp, setReloadTimestamp ] = useState(null)
  const [ loading, setLoading ] = useState(false)
  const [ date, setDate ] = useState(processDate(calendarConfig, initialDate))

  const condensedDates = useSelector(bookingSelectors.condensedDates)
  const rawData = useSelector(dataSelector)

  const data = useFilteredCalendarData({
    admin,
    config,
    data: rawData,
    slotFilter,
  })
  
  const loadCalendar = useCallback(async (date, condensed, direction = 1, withLoading = true) => {
    if(withLoading) setLoading(true)
    let data = await dispatch(loadDataAction({
      project,
      bookingform,
      date,
      days,
      condensed,
      direction,
      admin,
    }))
    if(autoAdjustDate && data && data[0]) {
      setDate(data[0].date)
      onUpdateDate && onUpdateDate(data[0].date)
    }
    if(withLoading) setLoading(false)
    return data
  }, [
    project,
    bookingform,
    days,
    admin,
    autoAdjustDate,
  ])

  const onSetDate = useCallback((newDate) => {
    newDate = typeof(newDate) === 'string' ? newDate : dateUtils.dateToString(newDate)
    loadCalendar(newDate, condensed, dateUtils.isBefore(date, newDate) ? -1 : 1, true)
  }, [
    date,
    condensed,
  ])

  const move = useCallback((direction) => {
    if(condensed) {
      const currentIndex = condensedDates.indexOf(date)
      const newDate = condensedDates[currentIndex + direction]
      if(newDate) {
        loadCalendar(newDate, condensed, 1, true)
      }
    }
    else {
      const newDate = dateUtils.dateToString(dateUtils.addDays(dateUtils.getDate(date), direction))
      loadCalendar(newDate, condensed, 1, true)
    }
  }, [
    date,
    condensed,
    condensedDates,
  ])

  const reload = useCallback(() => {
    loadCalendar(date, condensed, 1, false)
  }, [
    date,
    condensed,
  ])

  // if the back / forward date buttons are enabled based on the current date
  const navigatorButtons = useMemo(() => {
    if(condensed) {
      const currentIndex = condensedDates.indexOf(date)
      const back = currentIndex > 0
      const forward = currentIndex >= 0 && currentIndex < condensedDates.length - 1
      return {
        back,
        forward,
      }
    }
    else {
      const back = calendarConfig.min && date && dateUtils.isBeforeOrSame(calendarConfig.min, date) ? false : true
      const forward = calendarConfig.max && date && dateUtils.isAfterOrSame(calendarConfig.max, date) ? false : true
      return {
        back,
        forward,
      }
    }
  }, [
    calendarConfig,
    date,
    condensed,
    condensedDates,
  ])

  const parsedDate = useMemo(() => {
    if(!date) return new Date()
    const ret = dateUtils.stringToDate(date)
    if(ret.toString() === 'Invalid Date') return new Date()
    return ret
  }, [
    date,
  ])

  const isActive = useMemo(() => {
    if(admin) return true
    if(calendarConfig.max && dateUtils.isAfterOrSame(calendarConfig.max, new Date())) return false
    return true
  }, [
    calendarConfig,
    admin,
  ])

  // the initial loader and reacts to changes in condensed
  useEffect(() => {
    // wait for the config to arrive
    if(!config) return
    if(!isActive) return
    loadCalendar(date, condensed, 1, true)
  }, [
    isActive,
    config,
    condensed,
    days,
  ])

  // setup the reload loop
  useEffect(() => {
    let intervalId = null
    if(reloadDelay) {
      intervalId = setInterval(() => {
        setReloadTimestamp(new Date().getTime())
      }, reloadDelay)
    }
    return () => {
      clearInterval(intervalId)
    }
  }, [])

  // runs the reload loop
  useEffect(() => {
    if(!reloadTimestamp) return
    //loadCalendar(date, condensed, 1, false)
  }, [
    reloadTimestamp,
  ])

  return {
    admin,
    isActive,
    loading,
    config,
    calendarConfig,
    condensedDates,
    data,
    rawData,
    date,
    parsedDate,
    navigatorButtons,
    setDate: onSetDate,
    move,
    reload,
    condensed,
    slotFilter,
  }
}

export default useCalendar